// Client-side page meta for SPA routes.
//
// The built shell (dist/index.html) carries the site-level title/OG tags, and
// scripts/generate-publication-og.mjs prerenders per-route HTML for crawlers
// that don't run JavaScript. This hook covers the human side: it updates
// document.title and the head's description/OG/Twitter tags on client-side
// navigation, and restores the previous values on unmount so leaving the page
// never strands stale meta.

import { useEffect } from 'react'

export interface PageMeta {
  /** Full document title, e.g. "For GSAs — AgroLense". */
  title: string
  description: string
  /** Canonical absolute URL for og:url. */
  url: string
}

const META_SELECTORS: ReadonlyArray<[selector: string, key: keyof PageMeta]> = [
  ['meta[name="description"]', 'description'],
  ['meta[property="og:title"]', 'title'],
  ['meta[property="og:description"]', 'description'],
  ['meta[property="og:url"]', 'url'],
  ['meta[name="twitter:title"]', 'title'],
  ['meta[name="twitter:description"]', 'description'],
]

export function usePageMeta({ title, description, url }: PageMeta): void {
  useEffect(() => {
    const values: PageMeta = { title, description, url }
    const prevTitle = document.title
    document.title = title

    const restores: Array<() => void> = []
    for (const [selector, key] of META_SELECTORS) {
      const el = document.head.querySelector<HTMLMetaElement>(selector)
      if (!el) continue
      const prev = el.getAttribute('content')
      el.setAttribute('content', values[key])
      restores.push(() => {
        if (prev === null) el.removeAttribute('content')
        else el.setAttribute('content', prev)
      })
    }

    return () => {
      document.title = prevTitle
      for (const restore of restores) restore()
    }
  }, [title, description, url])
}
